import { createSlice } from '@reduxjs/toolkit';
import { KlineData, KlineInterval } from '@synfutures/sdks-perp-datasource';
import _ from 'lodash';

import { FETCHING_STATUS } from '@/constants';
import { IDepthChartData } from '@/types/chart';
import { ListArrayStatus, getDefaultListArrayStatus, setFulfilledListArrayStatus } from '@/types/redux';
import { getPairId } from '@/utils/transform/transformId';

import { AppState } from '../store';
import { getPairKlineData } from './actions';

export interface IChartState {
  chainOrderBookData: {
    [chainID: number]: {
      [pairId: string]: { [stepRatio: string]: IDepthChartData };
    };
  };
  chainOrderBookDataFetchingStatus: {
    [chainID: number]: {
      [pairId: string]: { [stepRatio: string]: FETCHING_STATUS };
    };
  };

  chainKlineData: {
    [chainID: number]: {
      [pairId: string]: { [chartDuration in KlineInterval]: ListArrayStatus<KlineData> };
    };
  };
  chainKlineDataIsFirstTimeLoad: {
    [chainID: number]: {
      [pairId: string]: { [chartDuration in KlineInterval]: boolean };
    };
  };
}

const initialState: IChartState = {
  chainOrderBookData: {},
  chainKlineData: {},
  chainKlineDataIsFirstTimeLoad: {},
  chainOrderBookDataFetchingStatus: {},
};

export const chartSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(getPairKlineData.pending, (state, action) => {
        const { chainId, instrumentAddr, expiry, chartDuration } = action.meta.arg;
        const pairId = getPairId(instrumentAddr, expiry);
        if (!_.get(state.chainKlineData, [chainId, pairId, chartDuration])) {
          _.set(
            state.chainKlineData,
            [chainId, pairId, chartDuration],
            getDefaultListArrayStatus(FETCHING_STATUS.FETCHING),
          );
          _.set(state.chainKlineDataIsFirstTimeLoad, [chainId, pairId, chartDuration], true);
        } else {
          _.set(state.chainKlineData, [chainId, pairId, 'status'], FETCHING_STATUS.FETCHING);
          _.set(state.chainKlineDataIsFirstTimeLoad, [chainId, pairId, chartDuration], false);
        }
      })
      .addCase(getPairKlineData.fulfilled, (state, action) => {
        const { chainId, instrumentAddr, expiry, chartDuration } = action.meta.arg;
        if (action.payload) {
          const pairId = getPairId(instrumentAddr, expiry);
          const klineData = _.get(state.chainKlineData, [chainId, pairId, chartDuration, 'list'], []);
          const list = _.chain([...klineData, ...action.payload])
            .groupBy('timestamp')
            .map((group) => _.merge({}, ...group))
            .orderBy(['timestamp'], ['asc'])
            .value();

          _.set(state.chainKlineData, [chainId, pairId, chartDuration], setFulfilledListArrayStatus(list));
        }
      })
      .addCase(getPairKlineData.rejected, (state, action) => {
        const { chainId, instrumentAddr, expiry, chartDuration } = action.meta.arg;
        if (action) {
          const pairId = getPairId(instrumentAddr, expiry);
          _.set(state.chainKlineData, [chainId, pairId, chartDuration, 'status'], FETCHING_STATUS.DONE);
        }
      });
  },
});

export const selectOrderBookData =
  (chainId: number | undefined, pairId: string | undefined, stepRatio: number | undefined) =>
  (state: AppState): IDepthChartData =>
    _.get(state.chart.chainOrderBookData, [chainId || '', pairId || '', `${stepRatio}_step` || '']);
export const selectOrderBookDataStatus =
  (chainId: number | undefined, pairId: string | undefined, stepRatio: number | undefined) =>
  (state: AppState): FETCHING_STATUS =>
    _.get(
      state.chart.chainOrderBookDataFetchingStatus,
      [chainId || '', pairId || '', `${stepRatio}_step` || ''],
      FETCHING_STATUS.INIT,
    );

export const selectKlineChartData =
  (chainId: number | undefined, pairId: string | undefined, chartDuration: KlineInterval) =>
  (state: AppState): KlineData[] =>
    _.get(state.chart.chainKlineData, [chainId || '', pairId || '', chartDuration, 'list']);

export const selectKlineChartFetchingStatus =
  (chainId: number | undefined, pairId: string | undefined, chartDuration: KlineInterval) =>
  (state: AppState): FETCHING_STATUS =>
    _.get(state.chart.chainKlineData, [chainId || '', pairId || '', chartDuration, 'status'], FETCHING_STATUS.INIT);

export const selectKlineChartFetchingFirstTime =
  (chainId: number | undefined, pairId: string | undefined, chartDuration: KlineInterval) =>
  (state: AppState): boolean =>
    _.get(state.chart.chainKlineDataIsFirstTimeLoad, [chainId || '', pairId || '', chartDuration], true);

export default chartSlice.reducer;
